/**
 * Tiny search language used by every searchable table, kept compatible with the
 * previous cockpit: `agoda successRatio>10 openRatio<50 !ubio status=closed`.
 *
 * - `term`        : the row contains the text (case-insensitive) in any field
 * - `!term`       : the row does not contain the text
 * - `prop>10`     : numeric comparison (`<`, `>`, `<=`, `>=`, `=`, `==`, `!=`)
 * - `prop=text`   : substring match on that field (`!=` negates)
 */
export type SearchRecord = Record<string, string | number | boolean | null | undefined>

type Criterion =
  | { kind: 'text'; negate: boolean; value: string }
  | { kind: 'compare'; prop: string; operator: string; value: string }

const COMPARISON = /^(\w+)(!=|<=|>=|==|=|<|>)(.+)$/
const TEXT = /^(!?)([\w.-]+)$/

export function parseFilter(expression: string): Criterion[] {
  return expression
    .split(/\s+/)
    .filter(Boolean)
    .map((token): Criterion | null => {
      const compare = COMPARISON.exec(token)
      if (compare) return { kind: 'compare', prop: compare[1]!, operator: compare[2]!, value: compare[3]! }
      const text = TEXT.exec(token)
      if (text) return { kind: 'text', negate: text[1] === '!', value: text[2]!.toLowerCase() }
      return null
    })
    .filter((c): c is Criterion => c !== null)
}

function findValue(record: SearchRecord, prop: string): SearchRecord[string] {
  if (prop in record) return record[prop]
  const key = Object.keys(record).find((k) => k.toLowerCase() === prop.toLowerCase())
  return key === undefined ? undefined : record[key]
}

function compareNumbers(actual: number, operator: string, expected: number): boolean {
  switch (operator) {
    case '>':
      return actual > expected
    case '<':
      return actual < expected
    case '>=':
      return actual >= expected
    case '<=':
      return actual <= expected
    case '=':
    case '==':
      return actual === expected
    case '!=':
      return actual !== expected
    default:
      return false
  }
}

function matchesCriterion(record: SearchRecord, criterion: Criterion): boolean {
  if (criterion.kind === 'text') {
    const haystack = Object.values(record)
      .filter((v) => v !== null && v !== undefined)
      .map((v) => String(v).toLowerCase())
      .join('\u0000')
    const contains = haystack.includes(criterion.value)
    return criterion.negate ? !contains : contains
  }

  const actual = findValue(record, criterion.prop)
  if (actual === null || actual === undefined || actual === '') return false

  const expectedNumber = Number(criterion.value)
  const actualNumber = typeof actual === 'number' ? actual : Number(actual)
  if (!Number.isNaN(expectedNumber) && !Number.isNaN(actualNumber)) {
    return compareNumbers(actualNumber, criterion.operator, expectedNumber)
  }

  const contains = String(actual).toLowerCase().includes(criterion.value.toLowerCase())
  if (criterion.operator === '!=') return !contains
  if (criterion.operator === '=' || criterion.operator === '==') return contains
  return false
}

export function matchesFilter(record: SearchRecord, criteria: Criterion[]): boolean {
  return criteria.every((c) => matchesCriterion(record, c))
}

export function filterRecords<T>(
  items: T[],
  expression: string,
  toRecord: (item: T) => SearchRecord,
): T[] {
  const criteria = parseFilter(expression)
  if (criteria.length === 0) return items
  return items.filter((item) => matchesFilter(toRecord(item), criteria))
}

/** Adds or removes a preset expression from the search box text. */
export function togglePreset(current: string, preset: string): string {
  const next = current.includes(preset)
    ? current.replace(preset, '')
    : `${current} ${preset}`
  return next.replace(/\s+/g, ' ').trim()
}
